// @flow
import React, { useRef, useEffect, useState, useMemo } from 'react';
import Helmet from 'react-helmet';
import { compose } from 'redux';
import isEmpty from '@justpark/helpers/src/isEmpty/isEmpty';
import { connect } from 'react-redux';
import {
  useParams,
  useLocation,
  useNavigate,
  useLoaderData
} from 'react-router-dom';
import loadable from '@loadable/component';
import LazyLoad from 'react-lazyload';
import bem from '@justpark/helpers/src/bem/bem';
import pngPixel from '@justpark/helpers/src/pngPixel/pngPixel';
import convertToDayjs from '@justpark/helpers/src/dates/convertToDayjs';
import config from '../../config/config';
import ConnectedSearchForm from '../components/Search/Form/SearchForm';
import pmeFind from '../../static/images/find_parking.png';
import pmeReserve from '../../static/images/reserve.png';
import pmePark from '../../static/images/park_car.png';
import appTwoSides from '../../static/images/justpark_app_two_sides.png';
import AppFeature from '../components/Common/AppFeature';
import Media from '../components/Common/Media';
import ThreeFeatureSlider from '../components/Common/ThreeFeatureSlider';
import withAnalytics from '../components/Decorators/withAnalytics';
import ContentModule from '../components/Common/ContentModule';
import ToggleLandingInfo from '../components/LandingPage/ToggleLandingInfo';
import NearbyLocations from '../components/LandingPage/NearbyLocations';
import SubsectionsPanel from '../components/Common/Subsections/SubsectionsPanel';
import { track } from '../helpers/Tracking/Analytics';
import FancyBanner from '../components/UI/FancyBanner';
import getMonthlyDuration from '../helpers/getMonthlyDuration';
import { defaultSearchDates } from '../helpers/date';
import HomePhoneSmallWebp from '../../static/images/home_phone_small.webp';
import HomePhoneWebp from '../../static/images/home_phone.webp';
import HomePhonePng from '../../static/images/home_phone.png';
import HomePhoneSmallAvif from '../../static/images/home_phone_small.avif';
import HomePhoneAvif from '../../static/images/home_phone.avif';
import useDarkly from '../hooks/useDarkly';
import SSRedirect from '../components/Common/SSRedirect';
import NoCacheSnackBar from '../components/NoCacheSnackBar';

import '../../static/scss/components/hero.scss';
import '../../static/scss/pages/landing.scss';

type ThreeFeatureSliderProps = {
  name: string,
  introTitle: string,
  intro: string,
  isMobile: boolean
};

type Props = {
  // eslint-disable-next-line react/no-unused-prop-types
  analytics: any,
  browser?: any
};

type SearchFormProps = {
  navigate: () => void,
  name: string,
  preposition: string,
  hideInput: boolean,
  disableDefaultDates: boolean,
  modifier?: string,
  browser: any,
  setRef: (any) => void,
  location: any,
  dateChangeHandler: () => void,
  centerPoint: any,
  dates: any,
  setMonthlyType: () => void
};

const ThreeFeatureSliderPanel = ({
  name,
  introTitle,
  intro,
  isMobile
}: ThreeFeatureSliderProps) => {
  const introArray = intro
    .replace('</p>', '')
    .split('<p>')
    .filter(Boolean)
    .map((el) => `<p>${el}</p>`);
  return (
    <>
      <ThreeFeatureSlider modifier={{ 'parking-made-easy': true }}>
        <Media
          modifier="stacked"
          title={
            <>
              <b>Find</b> a parking space
            </>
          }
          image={pmeFind}
          width={360}
          height={357}
          nativeLazy
        >
          <ul>
            <li>
              <span>
                Wherever, whenever. Best choice of options for every journey
              </span>
            </li>
          </ul>
        </Media>
        <Media
          modifier="stacked"
          title={
            <>
              <b>Reserve</b> your space
            </>
          }
          image={pmeReserve}
          width={240}
          height={228}
          nativeLazy
        >
          <ul>
            <li>
              <span>
                It only takes a few clicks. Pay via card, Apple or Android pay
              </span>
            </li>
          </ul>
        </Media>
        <Media
          modifier="stacked"
          title={
            <>
              <b>Just park</b> your car
            </>
          }
          image={pmePark}
          width={360}
          height={363}
          nativeLazy
        >
          <ul>
            <li>
              <span>
                Follow simple directions to your space and get on with your day
              </span>
            </li>
          </ul>
        </Media>
      </ThreeFeatureSlider>
      <div className="c-intro-wrapper">
        <div className="c-intro-wrapper__container">
          <div className="c-intro">
            <div className="c-intro__img-wrapper">
              <Media image={appTwoSides} title={`Find parking in ${name}`} />
            </div>
            <div className="c-intro__info">
              <h2>
                {introTitle.replace('%name%', '')} <span>{name}</span>
              </h2>
              {typeof intro === 'string' && (
                <ToggleLandingInfo
                  toDisplay={1}
                  array={introArray}
                  hideIcons={isMobile}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const ReviewsPanel = loadable(
  () =>
    import(
      /* webpackChunkName: "component-reviews-panel" */ '../components/Common/Review/ReviewsPanel'
    ),
  {
    ssr: false
  }
);

const getInitialState = () => ({
  dates: { ...defaultSearchDates(), monthlyDuration: null }
});

const RenderSearchForm = ({
  preposition,
  name,
  centerPoint,
  hideInput = false,
  disableDefaultDates = false,
  modifier = 'single',
  dates,
  location,
  dateChangeHandler,
  browser,
  setRef,
  navigate,
  setMonthlyType
}: SearchFormProps) => (
  <ConnectedSearchForm
    placeholder={`Where would you like to park ${preposition} ${name}?`}
    modifier={modifier}
    location={location}
    browser={browser}
    noSubmit
    hideInput={hideInput}
    disableDefaultDates={disableDefaultDates}
    coords={centerPoint}
    allowScroll
    onDateChange={dateChangeHandler}
    dates={dates}
    cta="Show parking spaces"
    name={name}
    tabTheme="dark"
    geolocationEvent="home-landing"
    setRef={setRef}
    navigate={navigate}
    showRollingDate={false}
    setMonthlyType={setMonthlyType}
  />
);

const generateTitle = (heroTitle, titleElemClass, name) =>
  heroTitle.replace(
    '%name%',
    `<span class="title-name ${titleElemClass}" data-cy="landing-title">${name}</span>`
  );

const generateMobileTitle = (heroMobileTitle, titleElemClass, name) =>
  heroMobileTitle.replace(
    '%name%',
    `<span class="title-name ${titleElemClass}">${name}</span>`
  );

const baseClass = 'p-landing';

const useOnLandingMount = (props, slug, country) => {
  const [hasMounted, setHasMounted] = useState(false);

  const { analytics, geoLocation } = props;

  useEffect(() => {
    setHasMounted(true);

    if (!hasMounted) {
      track('ViewContent', {
        content_type: 'destination',
        content_ids: slug
      });

      if (geoLocation) {
        const { geoType } = geoLocation;

        // Used in GTM Events
        analytics.page('landing', {
          landing_slug: slug,
          geo_type: geoType
        });
      }
    }
  }, [setHasMounted, geoLocation, analytics, slug, country, hasMounted]);
};

const getBreadCrumbs = (name, slug, breadcrumbs) => {
  const defaultBreadcrumbs = [
    {
      title: 'JustPark',
      url: 'https://www.justpark.com/'
    },
    {
      title: `${name} parking`,
      url: `https://www.justpark.com/uk/parking/${slug}/`
    }
  ];

  const items =
    breadcrumbs?.length > 0
      ? [defaultBreadcrumbs[0], ...breadcrumbs]
      : defaultBreadcrumbs;

  const breadcrumbArray = items.map((breadcrumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: breadcrumb.title,
    item: breadcrumb.url
  }));

  return `{
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": ${JSON.stringify(breadcrumbArray)}
              }
          `;
};

const reviewsMapper = (reviews) =>
  reviews &&
  reviews.map((review) => ({
    name: review.sender.data.name,
    createdAt: convertToDayjs(review.date).format('D MMM YYYY'),
    sender: {
      photo: {
        square: {
          url: review.sender.data.profilePhoto
        }
      },
      firstName: review.sender.data.firstName
    },
    rating: review.rating,
    title: review.listing.data.displayName,
    comment: review.comment
  }));

export const Landing = (props: Props) => {
  const { browser } = props;
  const data = useLoaderData();

  const geoLocation = data?.geoLocation;
  const reviews = useMemo(() => reviewsMapper(geoLocation?.reviews?.data), [
    geoLocation?.reviews?.data
  ]);

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { slug, country } = params;
  const { search, hash, pathname } = location;

  useOnLandingMount(props, slug, country);

  const monthlyDuration = getMonthlyDuration(search);

  const [dates, setDates] = useState({
    ...getInitialState().dates,
    monthlyDuration,
    monthlyType: 'WEEKDAYS_ONLY'
  });

  const setMonthlyType = (monthlyType) => {
    setDates((newData) => ({
      ...newData,
      monthlyType
    }));
  };

  const [disableDefaultDates, setDisableDefaultDates] = useState(true);

  const formRef = useRef();

  const newGeoEnabled = useDarkly('enable-new-geo');

  // The feature flagged new geo service allows us to render more granular landing pages
  // If the page pathname has more than three segments, it's a new Geo Page
  // Unless the new Geo Service is on we should 404 those pages to match previous behaviour
  const pathParts = pathname.split('/').filter((str) => str.length > 0);
  const isNewGeoUrl = pathParts.length > 3;

  if (newGeoEnabled !== true && isNewGeoUrl === true) {
    return <SSRedirect url="/" status={302} />;
  }

  if (!geoLocation) {
    return null;
  }

  const {
    canonical,
    geoType,
    meta,
    heroTitle,
    heroMobileTitle,
    heroImage,
    heroDescription,
    intro,
    introTitle,
    name,
    preposition,
    subsections,
    amenities,
    nearbyLocationsTitle,
    nearbyLocations,
    centerPoint,
    breadcrumbs
  } = geoLocation;

  const dateChangeHandler = (newDates) => {
    const datesState = {
      arriving: newDates[0].arriving.toDate(),
      leaving: newDates[0].leaving ? newDates[0].leaving.toDate() : null,
      monthlyDuration: newDates[0].monthlyDuration || null
    };
    setDisableDefaultDates(false);
    setDates((newData) => ({
      ...newData,
      ...datesState
    }));
  };

  const heroStyles = heroImage
    ? {
        backgroundImage: `url(${heroImage.full.sourceUrl})`
      }
    : {};

  const activeTab = !isEmpty(hash) ? hash.slice(1) : '';
  const hasLongTitle =
    preposition && name && preposition.length + name.length > 12;
  const titleElemClass = hasLongTitle ? 'title-name__long' : '';
  const phoneElemClass = hasLongTitle ? 'c-hero__phone-img--long' : '';
  const generatedTitle = generateTitle(heroTitle, titleElemClass, name);

  const generatedMobileTitle = generateMobileTitle(
    heroMobileTitle || heroTitle,
    titleElemClass,
    name
  );

  const isMobile = browser.lessThan.medium;

  return (
    <div className={baseClass}>
      <NoCacheSnackBar />
      <Helmet titleTemplate="%s">
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />

        <link
          rel="preload"
          as="image"
          href={HomePhoneSmallAvif}
          type="image/avif"
          media="(max-width: 375px)"
        />
        <link
          rel="preload"
          as="image"
          href={HomePhoneAvif}
          type="image/avif"
          media="(min-width: 376px) (max-width: 639px)"
        />

        {heroImage && (
          <link
            rel="preload"
            as="image"
            href={heroImage.full.sourceUrl}
            type="image/jpg"
            media="(min-width: 640px)"
          />
        )}
        <script type="application/ld+json">
          {getBreadCrumbs(name, slug, breadcrumbs)}
        </script>
      </Helmet>
      <FancyBanner position="landing" />
      <ContentModule
        baseClassName="hero"
        modifier="landing"
        wrapperModifier={heroImage ? 'with-hero' : 'no-hero'}
        styles={heroStyles}
      >
        <div className="c-hero-content">
          <div className="c-hero-content__clear">
            <div className="c-hero__phone">
              <picture>
                <source
                  srcSet={HomePhoneSmallAvif}
                  type="image/avif"
                  media="(max-width: 375px)"
                />
                <source
                  srcSet={HomePhoneSmallWebp}
                  type="image/webp"
                  media="(max-width: 375px)"
                />
                <source
                  srcSet={HomePhoneAvif}
                  type="image/avif"
                  media="(max-width: 639px)"
                />
                <source
                  srcSet={HomePhoneWebp}
                  type="image/webp"
                  media="(max-width: 639px)"
                />
                <source
                  srcSet={HomePhonePng}
                  type="image/png"
                  media="(max-width: 639px)"
                />
                <source
                  srcSet={pngPixel()}
                  type="image/png"
                  media="(min-width: 40em)"
                />
                <img
                  className={`c-hero__phone-img ${phoneElemClass}`}
                  src={HomePhonePng}
                  alt="Mobile Phone"
                  fetchpriority="high"
                  width="466"
                  height="770"
                />
              </picture>
            </div>
            <div className="c-hero-content__inner">
              <div className="c-hero-content__overlay">
                <h1
                  className={bem(baseClass, 'title')}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: isMobile ? generatedMobileTitle : generatedTitle
                  }}
                />
                <p
                  className={`${bem(
                    baseClass,
                    'hero-description'
                  )} mobile-hidden`}
                >
                  {heroDescription}
                </p>
                <p className="mobile-hidden">
                  <strong>
                    Select the dates and times you’d like to park…
                  </strong>
                </p>
                <RenderSearchForm
                  preposition={preposition}
                  centerPoint={centerPoint}
                  name={name}
                  slug={slug}
                  detached
                  hideInput={geoType !== 'location'}
                  disableDefaultDate
                  props={props}
                  location={location}
                  dates={dates}
                  dateChangeHandler={dateChangeHandler}
                  browser={browser}
                  setRef={(ref) => {
                    formRef.current = ref;
                  }}
                  navigate={navigate}
                  disableDefaultDates={disableDefaultDates}
                  setMonthlyType={setMonthlyType}
                />
              </div>
            </div>
          </div>
        </div>
      </ContentModule>
      <div className="c-hero__three_feature_slider c-hero__three_feature_slider--visible">
        <ThreeFeatureSliderPanel
          name={name}
          introTitle={introTitle}
          intro={intro}
          isMobile={isMobile}
        />
      </div>
      <AppFeature name={name} />
      <LazyLoad once offset={200} height={740}>
        <ReviewsPanel
          modifier={config.app.consts.landingModifier}
          reviews={reviews}
          name={name}
        />
      </LazyLoad>
      <SubsectionsPanel
        activeTab={activeTab}
        subsections={subsections}
        amenities={amenities}
        subtext={params.slug}
        modifier={
          nearbyLocations && nearbyLocations.length ? 'nearby-locations' : ''
        }
      />
      <NearbyLocations
        nearbyLocations={nearbyLocations}
        nearbyLocationsTitle={nearbyLocationsTitle}
        name={name}
      />
    </div>
  );
};

Landing.defaultProps = {
  browser: {}
};

const mapStateToProps = (state) => ({
  browser: state.browser
});

export default compose(
  withAnalytics({}, {}),
  connect(mapStateToProps)
)(Landing);
